"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildPathInstances = exports.SEGMENT_FLOATS = void 0;
const maplibre_gl_1 = require("maplibre-gl");
const turf_1 = require("@turf/turf");
const geojson_validation_1 = require("geojson-validation");
const ColorUtils_1 = require("chaire-lib-common/lib/utils/ColorUtils");
/** MapLibre tile size: one Mercator unit is 512 pixels at zoom 0 */
const TILE_SIZE = 512;
/** Consecutive points closer than this, in Mercator units (about 4 cm at the equator), are merged */
const MIN_SEGMENT_LENGTH = 1e-9;
/** Fallback color for paths without a valid `color` property */
const DEFAULT_COLOR_HEX = '#808080';
/**
 * Floats per segment in `PathInstances.segments`: previous point x, y, start x, y, end x, y,
 * next point x, y, distances of the start and end from the path start
 */
exports.SEGMENT_FLOATS = 10;
/** Whether a feature is a valid GeoJSON LineString feature */
const isLineStringFeature = (feature) => (0, geojson_validation_1.isFeature)(feature) && (0, geojson_validation_1.isLineString)(feature.geometry);
/**
 * Convert to Mercator and drop the points at the same place as the previous one. The segment
 * between 2 such points has no length, so no direction to draw the line and its joins.
 * @param coordinates [longitude, latitude] of the LineString
 * @returns Points as [x, y, distance from the first point], in Mercator units
 */
const toDistinctMercatorPoints = (coordinates) => {
    const points = [];
    for (const [lng, lat] of coordinates) {
        const { x, y } = maplibre_gl_1.MercatorCoordinate.fromLngLat([lng, lat]);
        const previous = points[points.length - 1];
        if (previous === undefined) {
            points.push([x, y, 0]);
            continue;
        }
        const length = Math.hypot(x - previous[0], y - previous[1]);
        if (length >= MIN_SEGMENT_LENGTH) {
            points.push([x, y, previous[2] + length]);
        }
    }
    return points;
};
/**
 * Remove the details smaller than the tolerance (Douglas-Peucker). The kept points are the input
 * arrays, so they keep their distance along the original path.
 */
const simplifyPoints = (points, toleranceMercator) => toleranceMercator > 0 && points.length > 2
    ? (0, turf_1.simplify)((0, turf_1.lineString)(points), { tolerance: toleranceMercator, mutate: true }).geometry.coordinates
    : points;
/**
 * Build the instance buffers of the animated arrow path layer, one instance per segment, the
 * straight line between 2 consecutive points of a path. Each path restarts its distance at 0.
 * Features that are not line strings, or have fewer than 2 distinct points, are skipped.
 *
 * @param features GeoJSON features, LineStrings with an optional `color` property (hex or rgb(a) string)
 * @param simplifyTolerance Details smaller than this distance, in pixels at zoom 0, are removed.
 * Distances along the paths stay those of the original geometry. Defaults to 0, no simplification.
 * @returns The instance data, with the origin at the center of the bounding box of all paths (see
 * `PathInstances.origin` for the precision)
 */
const buildPathInstances = (features, simplifyTolerance = 0) => {
    const paths = features
        .filter(isLineStringFeature)
        .map((feature) => {
        var _a;
        return ({
            points: simplifyPoints(toDistinctMercatorPoints(feature.geometry.coordinates), simplifyTolerance / TILE_SIZE),
            color: (0, ColorUtils_1.hexToRgbArray)((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.color, DEFAULT_COLOR_HEX)
        });
    })
        .filter((path) => path.points.length >= 2);
    const count = paths.reduce((total, path) => total + path.points.length - 1, 0);
    const segments = new Float32Array(count * exports.SEGMENT_FLOATS);
    const colors = new Uint8Array(count * 4);
    if (count === 0) {
        return { origin: [0, 0], segments, colors, count };
    }
    const [minX, minY, maxX, maxY] = (0, turf_1.bbox)((0, turf_1.multiPoint)(paths.flatMap((path) => path.points)));
    const origin = [(minX + maxX) / 2, (minY + maxY) / 2];
    let segmentIndex = 0;
    for (const path of paths) {
        const lastIndex = path.points.length - 1;
        for (let i = 1; i <= lastIndex; i++) {
            const start = path.points[i - 1];
            const end = path.points[i];
            const previous = path.points[Math.max(i - 2, 0)];
            const next = path.points[Math.min(i + 1, lastIndex)];
            // Written in place, without temporary arrays: this runs for every segment of the data
            const offset = segmentIndex * exports.SEGMENT_FLOATS;
            segments[offset] = previous[0] - origin[0];
            segments[offset + 1] = previous[1] - origin[1];
            segments[offset + 2] = start[0] - origin[0];
            segments[offset + 3] = start[1] - origin[1];
            segments[offset + 4] = end[0] - origin[0];
            segments[offset + 5] = end[1] - origin[1];
            segments[offset + 6] = next[0] - origin[0];
            segments[offset + 7] = next[1] - origin[1];
            segments[offset + 8] = start[2] * TILE_SIZE;
            segments[offset + 9] = end[2] * TILE_SIZE;
            colors.set(path.color, segmentIndex * 4);
            segmentIndex++;
        }
    }
    return { origin, segments, colors, count };
};
exports.buildPathInstances = buildPathInstances;
//# sourceMappingURL=pathGeometry.js.map